/**
 * Per-user trip visibility — mirrors iOS TripAccessPolicy.
 * Owned by Cognito sub, or trip id in active share memberships.
 */

import type { Trip } from '../types/client';
import {
  activeSharedTripIdsFromMemberships,
  type TripMembership,
} from '../services/tripShareService';

const LAST_SIGNED_IN_SUB_KEY = 'wt_last_signed_in_sub';
const LAST_SIGNED_IN_SHARED_IDS_KEY = 'wt_last_signed_in_shared_canonical_ids';

let cachedActiveSharedTripIds: Set<string> = new Set();

/** Replace cached active shared trip ids (call after inbox refresh). */
export function setActiveSharedTripIds(ids: Set<string>): void {
  cachedActiveSharedTripIds = new Set(ids);
}

/** Update cache from membership list. */
export function setActiveSharedTripIdsFromMemberships(memberships: TripMembership[]): void {
  setActiveSharedTripIds(activeSharedTripIdsFromMemberships(memberships));
}

export function getActiveSharedTripIds(_userId?: string): Set<string> {
  return cachedActiveSharedTripIds;
}

/** Clear live share membership cache (logout / user switch). */
export function clearActiveSharedTripIds(): void {
  cachedActiveSharedTripIds = new Set();
}

/** Test helper — clear cache between tests. */
export function resetActiveSharedTripIdsForTests(): void {
  clearActiveSharedTripIds();
}

export function getLastSignedInSub(): string | null {
  try {
    const raw = localStorage.getItem(LAST_SIGNED_IN_SUB_KEY)?.trim();
    return raw ? raw : null;
  } catch {
    return null;
  }
}

/** Persist Cognito sub across logout for signed-out filtering (iOS lastSignedInSub). */
export function setLastSignedInSub(sub: string | null | undefined): void {
  try {
    const trimmed = sub != null ? String(sub).trim() : '';
    if (trimmed) {
      localStorage.setItem(LAST_SIGNED_IN_SUB_KEY, trimmed);
    }
  } catch {
    // ignore
  }
}

export function getLastSignedInSharedCanonicalIds(): Set<string> {
  try {
    const raw = localStorage.getItem(LAST_SIGNED_IN_SHARED_IDS_KEY);
    if (!raw) return new Set();
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) return new Set();
    return new Set(
      parsed.map((id) => String(id).trim()).filter((id) => id.length > 0)
    );
  } catch {
    return new Set();
  }
}

export function setLastSignedInSharedCanonicalIds(ids: Set<string>): void {
  try {
    const cleaned = Array.from(ids)
      .map((id) => String(id).trim())
      .filter((id) => id.length > 0)
      .sort();
    localStorage.setItem(LAST_SIGNED_IN_SHARED_IDS_KEY, JSON.stringify(cleaned));
  } catch {
    // ignore
  }
}

/**
 * On sign-out: snapshot live share ids for the last user, then clear the live cache
 * so the next signed-in user cannot inherit prior memberships.
 */
export function snapshotSharedTripIdsBeforeSignOut(): void {
  setLastSignedInSharedCanonicalIds(cachedActiveSharedTripIds);
  clearActiveSharedTripIds();
}

/**
 * On sign-in: record sub and clear live share cache when the signed-in user changes
 * so prior memberships cannot leak into the new session before inbox hydrate.
 */
export function prepareTripAccessForSignedInUser(sub: string): void {
  const next = String(sub ?? '').trim();
  if (!next) return;
  const prev = getLastSignedInSub();
  if (prev && prev !== next) {
    clearActiveSharedTripIds();
  }
  setLastSignedInSub(next);
}

function normalizeOwnerId(ownerId: string | null | undefined): string | null {
  if (ownerId == null) return null;
  const s = String(ownerId).trim();
  return s ? s : null;
}

export function isOwnedTrip(trip: Trip, sub: string): boolean {
  const owner = normalizeOwnerId(trip.ownerId);
  return owner != null && owner === sub;
}

export function isSharedTrip(trip: Trip, sub: string): boolean {
  const canonical = (trip as Trip & { canonicalTripId?: string | null }).canonicalTripId;
  if (!canonical || !String(canonical).trim()) return false;
  return !isOwnedTrip(trip, sub);
}

/**
 * Visible if owned by current sub, OR trip id is in active share memberships.
 * Nil ownerId is not accessible until claimed (claim runs before list/sync).
 */
export function isAccessibleTrip(
  trip: Trip,
  sub: string,
  activeSharedTripIds: Set<string> = getActiveSharedTripIds()
): boolean {
  if (isOwnedTrip(trip, sub)) return true;
  const id = String(trip.id ?? '').trim().toLowerCase();
  if (!id) return false;
  if (activeSharedTripIds.has(id) || activeSharedTripIds.has(trip.id)) return true;
  for (const shared of activeSharedTripIds) {
    if (shared.toLowerCase() === id) return true;
  }
  return false;
}

export function visibleTrips(
  trips: Trip[],
  sub: string,
  activeSharedTripIds: Set<string> = getActiveSharedTripIds(sub)
): Trip[] {
  return trips.filter((t) => isAccessibleTrip(t, sub, activeSharedTripIds));
}

export function canShowShareUI(trip: Trip, sub: string): boolean {
  return isOwnedTrip(trip, sub) && !isSharedTrip(trip, sub);
}

export function effectiveCanEdit(trip: Trip, sub: string): boolean {
  if (isOwnedTrip(trip, sub)) return trip.canEdit !== false;
  if (isSharedTrip(trip, sub)) return trip.canEdit === true;
  return trip.canEdit !== false;
}

export function canOpenAttachments(trip: Trip, sub: string): boolean {
  return isOwnedTrip(trip, sub);
}

export function attachmentOwnerOnlyMessage(): string {
  return 'Only the trip owner can open attachments';
}

/**
 * Assign ownerId = sub and canEdit = true only when ownerId is null/empty.
 * Never changes a trip that already has a different ownerId.
 */
export function claimNilOwnerTrips(trips: Trip[], sub: string): Trip[] {
  let changed = false;
  const next = trips.map((t) => {
    const owner = normalizeOwnerId(t.ownerId);
    if (owner != null) return t;
    changed = true;
    return { ...t, ownerId: sub, canEdit: true };
  });
  return changed ? next : trips;
}

export function claimNilOwnerTrip(trip: Trip, sub: string): Trip {
  const owner = normalizeOwnerId(trip.ownerId);
  if (owner != null) return trip;
  return { ...trip, ownerId: sub, canEdit: true };
}
